import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { NavLink, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSociete } from '../context/SocieteContext';
import { changePassword, getNotifications, markNotifLue, markAllNotifsLues, deleteNotif } from '../api';
import { useUnreadDiscussions } from '../hooks/useUnreadDiscussions';
import {
  LayoutDashboard, Calendar, Wrench, ShoppingCart, Package,
  Building2, BarChart2, Menu, LogOut, ChevronDown, ChevronRight,
  User, Settings, Receipt,
  Globe, MapPin, Check, X, KeyRound, Eye, EyeOff, Warehouse, Bell, ShoppingBag, Tablet, Users, Mail, ArrowLeftRight, MessageCircle, ExternalLink, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import { AnnouncementBanner } from './AnnouncementBanner';

const navItems = [
  { key: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard, path: '/dashboard' },
  { key: 'contacts', label: 'Annuaire', icon: Building2, path: '/annuaire' },
  {
    key: 'distribution', label: 'Distribution', icon: ShoppingCart, path: null,
    children: [
      { key: 'ventes',                label: 'Devis / Ventes',    path: '/ventes' },
      { key: 'bons-livraison-ventes', label: 'Bons de livraison', path: '/bons-livraison-ventes' },
      { key: 'retours-ventes',        label: 'Bons de retour',    path: '/retours-ventes' },
      { key: 'factures-distribution', label: 'Factures',          path: '/factures-distribution' },
      { key: 'commandes',             label: 'Commandes',         path: '/commandes' },
      { key: 'facturation',           label: 'Facturation',       path: '/facturation' },
    ]
  },
  {
    key: 'location_v2', label: 'Location', icon: Layers, path: null,
    children: [
      { key: 'contrats_v2',      label: 'Contrats',          path: '/location-v2/contrats' },
      { key: 'planning',         label: 'Planning',          path: '/location/planning' },
      { key: 'controles',        label: 'BL avec contrôles', path: '/location/controles' },
      { key: 'gammes_location',  label: 'Gammes',            path: '/location/gammes' },
    ]
  },
  {
    key: 'location', label: 'Location V1', icon: Calendar, path: null,
    children: [
      { key: 'contrats',        label: 'Contrats',    path: '/contrats' },
      { key: 'livraisons',      label: 'Livraisons',  path: '/location/livraisons' },
      { key: 'facturation_loc', label: 'Facturation', path: '/location/facturation' },
    ]
  },
  {
    key: 'atelier', label: 'Atelier', icon: Wrench, path: null,
    children: [
      { key: 'ordres_reparation',      label: 'Ordres de réparation',  path: '/atelier' },
      { key: 'tableau_de_charge',      label: 'Tableau de charge',     path: '/rh/tableau-de-charge' },
      { key: 'temps_non_facturables',  label: 'Temps non facturables', path: '/rh/tnf' },
      { key: 'plans_entretien',        label: "Maintenance Préventive", path: '/atelier/maintenance' },
      { key: 'atelier_factures',       label: 'Factures Atelier',      path: '/atelier/factures' },
    ]
  },
  {
    key: 'facturation_menu', label: 'Facturation', icon: Receipt, path: null,
    children: [
      { key: 'facturation_factures',   label: 'Factures client',   path: '/facturation/factures' },
      { key: 'facturation_reglements', label: 'Règlements client', path: '/facturation/reglements' },
      { key: 'rapports_relances',      label: 'Relances',          path: '/rapports/relances' },
    ]
  },
  {
    key: 'achats', label: 'Achats', icon: ShoppingBag, path: null,
    children: [
      { key: 'bons_achat',       label: "Commandes d'achat",  path: '/achats' },
      { key: 'bons_reception',   label: 'Bons de réception',  path: '/bons-reception' },
      { key: 'dossiers_revient', label: 'Dossiers de revient', path: '/achats/dossiers-revient' },
      { key: 'besoins_appro',    label: 'Approvisionnement',  path: '/achats/reassort' },
      { key: 'acomptes',         label: 'Acomptes / Virements', path: '/achats/acomptes' },
      { key: 'factures_achat',   label: 'Factures',           path: '/factures-achat' },
    ]
  },
  {
    key: 'materiels', label: 'Matériels', icon: Package, path: null,
    children: [
      { key: 'materiels_list', label: 'Matériels', path: '/materiels' },
      { key: 'marques_modeles', label: 'Marques & Modèles', path: '/marques-modeles' },
      { key: 'financements', label: 'Financements', path: '/financements' },
      { key: 'controles_periodiques', label: 'Contrôles périodiques', path: '/materiels/controles-periodiques' },
      { key: 'comptages', label: 'Comptages', path: '/materiels/comptages' },
    ]
  },
  {
    key: 'catalogue', label: 'Stocks', icon: Warehouse, path: null,
    children: [
      { key: 'articles',          label: 'Articles',            path: '/articles' },
      { key: 'familles',          label: 'Familles',            path: '/familles' },
      { key: 'unites_contenants', label: 'Unités & Contenants', path: '/unites-contenants' },
      { key: 'carac_techniques',  label: 'Caractéristiques techniques', path: '/carac-techniques' },
      { key: 'stocks_liste',        label: 'Gestion des stocks',  path: '/stocks' },
      { key: 'mouvements_zone',     label: 'Mouvements de stock', path: '/mouvements-zone' },
      { key: 'mouvements_stock',    label: 'Historique des mouvements', path: '/mouvements-stock' },
      { key: 'transferts',          label: 'Transferts',           path: '/transferts' },
      { key: 'inventaires',         label: 'Inventaires',         path: '/inventaires' },
      { key: 'stocks_valorisation', label: 'Valorisation',        path: '/stocks/valorisation' },
    ]
  },
  {
    key: 'rh', label: 'Ressources humaines', icon: Users, path: null,
    children: [
      { key: 'employes',           label: 'Employés',          path: '/rh/employes' },
      { key: 'fonctions_internes', label: 'Fonctions internes', path: '/rh/fonctions-internes' },
    ],
  },
  {
    key: 'rapports', label: 'Rapports', icon: BarChart2, path: null,
    children: [
      { key: 'rapports_materiels',          label: 'Matériels',         path: '/rapports/materiels' },
      { key: 'rapports_potentiel_locatif',  label: 'Potentiel locatif', path: '/rapports/potentiel-locatif' },
      { key: 'rapports_balances_agees',     label: 'Balances âgées',    path: '/rapports/balances-agees' },
      { key: 'rapports_palmares_clients',   label: 'Palmarès clients',  path: '/rapports/palmares-clients' },
      { key: 'rapports_previsions',         label: 'Prévisions tréso.', path: '/rapports/previsions' },
    ],
  },
  {
    key: 'emailing', label: 'Emailing', icon: Mail, path: null,
    children: [
      { key: 'email_campagnes', label: 'Campagnes',  path: '/emailing/campagnes' },
      { key: 'email_audiences', label: 'Audiences',  path: '/emailing/audiences' },
      { key: 'email_modeles',   label: 'Modèles',    path: '/emailing/modeles'   },
      { key: 'email_logs',      label: 'Logs',       path: '/emailing/logs'      },
    ]
  },
  { key: 'transfert_comptable', label: 'Transfert comptable', icon: ArrowLeftRight, path: '/transfert-comptable' },
];

// ─── Modal changement de mot de passe ────────────────────────────────────────
function ChangePasswordModal({ onClose }) {
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPwd !== confirmPwd) { toast.error('Les mots de passe ne correspondent pas'); return; }
    setSaving(true);
    try {
      await changePassword({ current_password: currentPwd, new_password: newPwd });
      toast.success('Mot de passe modifié avec succès');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden" style={{ background: 'white' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border-light)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'var(--primary-gradient)' }}>
              <KeyRound size={16} className="text-white" />
            </div>
            <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
              Changer le mot de passe
            </h2>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 hover:bg-gray-100 transition-colors">
            <X size={18} style={{ color: 'var(--text-muted)' }} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Mot de passe actuel */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5"
              style={{ color: 'var(--text-muted)' }}>Mot de passe actuel</label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPwd}
                onChange={e => setCurrentPwd(e.target.value)}
                data-testid="current-password-input"
                required
                className="w-full px-3 py-2.5 pr-10 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100"
                style={{ borderColor: 'var(--border-light)', color: 'var(--text-primary)' }}
              />
              <button type="button" onClick={() => setShowCurrent(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--text-muted)' }}>
                {showCurrent ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          {/* Nouveau mot de passe */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5"
              style={{ color: 'var(--text-muted)' }}>Nouveau mot de passe</label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPwd}
                onChange={e => setNewPwd(e.target.value)}
                data-testid="new-password-input"
                required
                className="w-full px-3 py-2.5 pr-10 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100"
                style={{ borderColor: 'var(--border-light)', color: 'var(--text-primary)' }}
              />
              <button type="button" onClick={() => setShowNew(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--text-muted)' }}>
                {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            <PasswordStrengthIndicator password={newPwd} />
          </div>

          {/* Confirmer */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5"
              style={{ color: 'var(--text-muted)' }}>Confirmer le nouveau</label>
            <input
              type={showNew ? 'text' : 'password'}
              value={confirmPwd}
              onChange={e => setConfirmPwd(e.target.value)}
              data-testid="confirm-password-input"
              required
              className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100"
              style={{
                borderColor: confirmPwd && confirmPwd !== newPwd ? '#EF4444' : 'var(--border-light)',
                color: 'var(--text-primary)'
              }}
            />
            {confirmPwd && confirmPwd !== newPwd && (
              <p className="text-xs mt-1" style={{ color: '#EF4444' }}>Les mots de passe ne correspondent pas</p>
            )}
          </div>

          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={saving || !currentPwd || !newPwd || !confirmPwd}
              data-testid="save-password-btn"
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: 'var(--primary-gradient)' }}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-sm font-medium"
              style={{ color: 'var(--text-muted)', border: '1px solid var(--border-light)' }}>
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout, menuRights } = useAuth();
  const { societes, activeSociete, activeAgences, selectSociete, clearSociete, selectSocieteWithAgences } = useSociete(); // eslint-disable-line no-unused-vars
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expanded, setExpanded] = useState({});

  // Auto-ouvrir la section du menu dont un enfant correspond à la route active
  useEffect(() => {
    const active = navItems.find(item =>
      item.children?.some(c => location.pathname.startsWith(c.path))
    );
    if (active) {
      setExpanded(e => e[active.key] ? e : { ...e, [active.key]: true });
    }
  }, [location.pathname]);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);

  // ── Redirection dashboard 2×/jour (matin + après 13h) ─────────────────────
  useEffect(() => {
    const slot = (() => {
      const now = new Date();
      const d   = now.toISOString().slice(0, 10);           // YYYY-MM-DD
      return `${d}-${now.getHours() < 13 ? 'am' : 'pm'}`;  // ex: 2026-05-07-am
    })();
    const key = 'last_dashboard_slot';
    if (localStorage.getItem(key) !== slot) {
      localStorage.setItem(key, slot);
      navigate('/dashboard', { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Discussions non-lues ────────────────────────────────────────────────────
  const { totalUnread: totalUnreadDiscussions, unread: unreadThreads } = useUnreadDiscussions(!!user);

  const DISC_NAV_MAP = {
    vente:      (id) => `/ventes?open_devis=${id}`,
    location:   (id) => `/contrats?contrat_id=${id}`,
    reparation: (id) => `/atelier?open_or=${id}`,
    achat:      (id) => `/achats?open_achat=${id}`,
    entreprise: (id) => `/annuaire?open_ent=${id}`,
  };

  // ── Notifications ──────────────────────────────────────────────────────────
  const [notifs, setNotifs]         = useState([]);
  const [nonLues, setNonLues]       = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const notifMenuRef                = useRef(null);

  const fetchNotifs = async () => {
    try {
      const r = await getNotifications();
      setNotifs(r.data.notifications?.filter(n => n.type !== 'discussion_message') || []);
      setNonLues(r.data.notifications?.filter(n => n.type !== 'discussion_message' && !n.lu).length || 0);
    } catch { /* silencieux */ }
  };

  // Polling toutes les 30 secondes
  useEffect(() => {
    fetchNotifs();
    const id = setInterval(fetchNotifs, 30000);
    return () => clearInterval(id);
  }, []);

  // Fermer dropdown en cliquant ailleurs
  useEffect(() => {
    const h = (e) => { if (notifMenuRef.current && !notifMenuRef.current.contains(e.target)) setShowNotifs(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleNotifClick = async (n) => {
    if (!n.lu) {
      await markNotifLue(n.notif_id).catch(() => {});
      setNotifs(prev => prev.map(x => x.notif_id === n.notif_id ? { ...x, lu: true } : x));
      setNonLues(v => Math.max(0, v - 1));
    }
    if (n.lien) navigate(n.lien);
    setShowNotifs(false);
  };

  const handleDeleteNotif = async (e, notif_id, lu) => {
    e.stopPropagation();
    try { await deleteNotif(notif_id); } catch {}
    setNotifs(prev => prev.filter(x => x.notif_id !== notif_id));
    if (!lu) setNonLues(v => Math.max(0, v - 1));
  };

  const handleMarkAll = async () => {
    await markAllNotifsLues().catch(() => {});
    setNotifs(prev => prev.map(x => ({ ...x, lu: true })));
    setNonLues(0);
  };
  const switcherRef = useRef(null);
  // ── Draft agences : accumule les sélections, flush à la fermeture du panneau ──
  const [draftAgences, setDraftAgences] = useState(null); // null = panneau fermé
  const displayAgences = draftAgences !== null ? draftAgences : activeAgences;

  const openSwitcher = () => {
    setDraftAgences(activeAgences); // snapshot à l'ouverture
    setSwitcherOpen(true);
  };

  const closeSwitcher = useCallback(() => {
    if (draftAgences !== null && activeSociete) {
      // Vérifier s'il y a eu un changement réel avant de flush
      const same =
        draftAgences.length === activeAgences.length &&
        draftAgences.every(d => activeAgences.some(a => a.agence_id === d.agence_id));
      if (!same) selectSocieteWithAgences(activeSociete, draftAgences);
    }
    setDraftAgences(null);
    setSwitcherOpen(false);
  }, [draftAgences, activeAgences, activeSociete, selectSocieteWithAgences]);

  const toggleDraftAgence = (agc) => {
    setDraftAgences(prev => {
      const current = prev ?? activeAgences;
      const exists = current.some(a => a.agence_id === agc.agence_id);
      return exists
        ? current.filter(a => a.agence_id !== agc.agence_id)
        : [...current, agc];
    });
  };

  const selectAllDraft = (agences) => setDraftAgences(agences);
  const clearDraft = () => setDraftAgences([]);

  // Redirection auto pour les utilisateurs uniquement "terrain"
  useEffect(() => {
    if (!menuRights || !Array.isArray(menuRights)) return;
    const isAdmin = user?.role === 'admin_org' || user?.role === 'super_admin';
    if (isAdmin) return;
    const terrainRights = menuRights.filter(r => r.startsWith('terrain_'));
    const standardRights = menuRights.filter(r => !r.startsWith('terrain_'));
    if (terrainRights.length > 0 && standardRights.length === 0) {
      navigate('/terrain', { replace: true });
    }
  }, [menuRights, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredNavItems = useMemo(() => {
    const isAdmin = user?.role === 'admin_org' || user?.role === 'super_admin';
    if (isAdmin) return navItems;
    if (menuRights === null) return [];
    if (!Array.isArray(menuRights)) return navItems;
    return navItems
      .map(item => {
        if (!item.children) return menuRights.includes(item.key) ? item : null;
        const visibleChildren = item.children.filter(c => menuRights.includes(c.key));
        if (visibleChildren.length === 0) return null;
        return { ...item, children: visibleChildren };
      })
      .filter(Boolean);
  }, [user?.role, menuRights]);

  useEffect(() => {
    const h = (e) => { if (switcherRef.current && !switcherRef.current.contains(e.target)) closeSwitcher(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [closeSwitcher]);

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin_org';

  const toggleSection = (key) => setExpanded(e => {
    const isOpen = e[key];
    const allClosed = Object.fromEntries(Object.keys(e).map(k => [k, false]));
    return { ...allClosed, [key]: !isOpen };
  });

  const handleLogout = async () => {
    await logout();
    toast.success('Déconnexion réussie');
    navigate('/login');
  };

  // ── Popover utilisateur ────────────────────────────────────────────────────
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setShowUserMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const UserPopover = () => (
    <div ref={userMenuRef} className="relative">
      {/* Déclencheur */}
      <button
        onClick={() => setShowUserMenu(v => !v)}
        className="w-full flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-white/10 transition-colors text-left"
        data-testid="user-menu-btn">
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center overflow-hidden flex-shrink-0">
          {user?.picture
            ? <img src={user.picture} alt="" className="w-full h-full object-cover" />
            : <User size={16} className="text-white" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-xs font-medium truncate">{user?.name}</p>
          <p className="text-white/50 text-xs truncate">{user?.email}</p>
        </div>
        <ChevronDown size={13} className={`text-white/40 flex-shrink-0 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
      </button>

      {/* Popover */}
      {showUserMenu && (
        <div className="absolute bottom-full left-0 mb-2 w-56 bg-white rounded-xl shadow-2xl border overflow-hidden z-50"
          style={{ borderColor: 'var(--border)' }}>
          {/* En-tête */}
          <div className="px-3 py-2.5 border-b" style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
            <p className="text-xs font-semibold truncate" style={{ color: 'var(--text)' }}>{user?.name}</p>
            <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{user?.email}</p>
          </div>
          {/* Actions */}
          <div className="py-1">
            <NavLink to="/mes-modeles-emails"
              onClick={() => setShowUserMenu(false)}
              className="flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 transition-colors w-full text-left"
              style={{ color: 'var(--text)' }}
              data-testid="user-menu-modeles-emails">
              <Mail size={14} style={{ color: 'var(--primary-light)' }} />
              Modèles d'emails
            </NavLink>
            <button
              onClick={() => { setShowUserMenu(false); setShowChangePwd(true); }}
              className="flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 transition-colors w-full text-left"
              style={{ color: 'var(--text)' }}
              data-testid="user-menu-change-pwd">
              <KeyRound size={14} style={{ color: 'var(--text-muted)' }} />
              Changer le mot de passe
            </button>
            <div className="border-t my-1" style={{ borderColor: 'var(--border)' }} />
            <button
              onClick={() => { setShowUserMenu(false); handleLogout(); }}
              className="flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-red-50 transition-colors w-full text-left"
              style={{ color: '#DC2626' }}
              data-testid="user-menu-logout">
              <LogOut size={14} />
              Se déconnecter
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo / Org Header */}
      <div className="px-4 py-4 border-b border-white/10" data-testid="sidebar-org-header">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(255,255,255,0.15)' }}>
            <Wrench size={18} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-white text-sm font-bold truncate block">
              {user?.org?.nom || 'Bedee'}
            </span>
            {isSuperAdmin && (
              <span className="text-white/50 text-xs">Super-Admin</span>
            )}
          </div>
        </div>
      </div>

      {/* Switcher Société / Agence */}
      {societes.length > 0 && (
        <div className="px-3 py-2 border-b border-white/10 relative" ref={switcherRef}>
          <button
            onClick={() => switcherOpen ? closeSwitcher() : openSwitcher()}
            data-testid="societe-switcher-btn"
            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium transition-all hover:bg-white/10"
            style={{ color: 'rgba(255,255,255,0.85)' }}>
            <Globe size={14} className="flex-shrink-0" />
            <div className="flex-1 text-left min-w-0">
              <span className="truncate block font-semibold">
                {activeSociete ? activeSociete.nom : 'Toutes les sociétés'}
              </span>
              {activeSociete && activeAgences.length > 0 && (
                <span className="text-white/50" style={{ fontSize: 10 }}>
                  {activeAgences.length === 1
                    ? activeAgences[0].nom
                    : activeAgences.length === (activeSociete.agences?.length || 0)
                      ? 'Toutes les agences'
                      : `${activeAgences.length} agences`}
                </span>
              )}
              {activeSociete && activeAgences.length === 0 && (
                <span className="text-white/50" style={{ fontSize: 10 }}>Toutes les agences</span>
              )}
            </div>
            <ChevronDown size={12} className={`flex-shrink-0 transition-transform ${switcherOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown */}
          {switcherOpen && (
            <div className="absolute left-3 right-3 top-full mt-1 z-50 bg-white rounded-xl shadow-xl border overflow-hidden"
              style={{ borderColor: 'var(--border)' }}>
              {/* Option "Toutes" */}
              <button
                onClick={() => { clearSociete(); setDraftAgences(null); setSwitcherOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-xs font-semibold hover:bg-gray-50 border-b"
                style={{ borderColor: 'var(--border-light)', color: !activeSociete ? 'var(--primary-solid)' : 'var(--text-secondary)' }}>
                <Globe size={13} />
                <span className="flex-1 text-left">Toutes les sociétés</span>
                {!activeSociete && <Check size={12} />}
              </button>

              {/* Liste des sociétés */}
              {[...societes].sort((a, b) => a.nom.localeCompare(b.nom, 'fr')).map(soc => {
                const isActive = activeSociete?.societe_id === soc.societe_id;
                const agences = [...(soc.agences || [])].sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
                return (
                  <div key={soc.societe_id}>
                    {/* Société */}
                    <button
                      onClick={() => { selectSociete(soc); setDraftAgences([]); }}
                      data-testid={`societe-option-${soc.societe_id}`}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold hover:bg-blue-50 border-b"
                      style={{ borderColor: 'var(--border-light)', background: isActive ? 'var(--primary-50)' : '', color: isActive ? 'var(--primary-solid)' : 'var(--text)' }}>
                      <Building2 size={13} />
                      <span className="flex-1 text-left truncate">{soc.nom}</span>
                      {isActive && <Check size={12} style={{ color: 'var(--primary-solid)' }} />}
                    </button>

                    {/* Agences — toujours visibles */}
                    {agences.length > 0 && (
                      <div className="bg-gray-50/80 border-b" style={{ borderColor: 'var(--border-light)' }}>
                        {/* "Toutes les agences" */}
                        <button
                          onClick={() => {
                            if (isActive) {
                              displayAgences.length === agences.length ? clearDraft() : selectAllDraft(agences);
                            } else {
                              selectSocieteWithAgences(soc, agences);
                              setDraftAgences(agences);
                              setSwitcherOpen(false);
                            }
                          }}
                          className="w-full flex items-center gap-2 pl-7 pr-3 py-1.5 text-xs hover:bg-white/80"
                          style={{ color: 'var(--text-muted)' }}>
                          <MapPin size={10} />
                          <span className="flex-1 text-left italic">Toutes les agences</span>
                          {isActive && displayAgences.length === agences.length
                            ? <Check size={11} style={{ color: 'var(--primary-solid)' }} />
                            : <div className="w-3 h-3 rounded border" style={{ borderColor: 'var(--text-disabled)' }} />}
                        </button>
                        {agences.map(agc => {
                          const agActive = isActive && displayAgences.some(a => a.agence_id === agc.agence_id);
                          return (
                            <button
                              key={agc.agence_id}
                              onClick={() => {
                                if (isActive) {
                                  toggleDraftAgence(agc);
                                } else {
                                  selectSocieteWithAgences(soc, [agc]);
                                  setDraftAgences([agc]);
                                  setSwitcherOpen(false);
                                }
                              }}
                              data-testid={`agence-option-${agc.agence_id}`}
                              className="w-full flex items-center gap-2 pl-7 pr-3 py-1.5 text-xs hover:bg-white/80"
                              style={{ color: agActive ? 'var(--primary-solid)' : 'var(--text-secondary)' }}>
                              <MapPin size={10} />
                              <span className="flex-1 text-left truncate">{agc.nom}</span>
                              {agActive
                                ? <Check size={11} style={{ color: 'var(--primary-solid)' }} />
                                : <div className="w-3 h-3 rounded border" style={{ borderColor: 'var(--text-disabled)' }} />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Gérer les sociétés */}
              <NavLink to="/settings/societes"
                onClick={() => closeSwitcher()}
                className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-50 border-t"
                style={{ borderColor: 'var(--border-light)', color: 'var(--text-muted)' }}>
                <Settings size={11} /> Gérer les sociétés & agences
              </NavLink>
            </div>
          )}
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        {filteredNavItems.map(item => {
          if (!item.children) {
            return (
              <NavLink
                key={item.key}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive: a }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${a ? 'bg-white/20 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'}`
                }
               
                data-testid={`nav-${item.key}`}
              >
                <item.icon size={18} />
                {item.label}
              </NavLink>
            );
          }
          const isOpen = expanded[item.key];
          const hasActiveChild = item.children.some(c => c.path === location.pathname);
          return (
            <div key={item.key}>
              <button
                onClick={() => toggleSection(item.key)}
                data-testid={`nav-section-${item.key}`}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${hasActiveChild ? 'bg-white/10 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}
               
              >
                <div className="flex items-center gap-3">
                  <item.icon size={18} />
                  {item.label}
                </div>
                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              {isOpen && (
                <div className="ml-4 mt-1 space-y-0.5 border-l border-white/10 pl-3">
                  {item.children.map(child => (
                    <NavLink
                      key={child.path || child.key}
                      to={child.path}
                      end
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive: a }) =>
                        `flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-all ${a ? 'bg-white/20 text-white font-medium' : 'text-white/60 hover:bg-white/10 hover:text-white'}`
                      }
                     
                      data-testid={`nav-${child.path ? child.path.replace(/\//g, '-').slice(1) : child.key}`}
                    >
                      {child.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="px-3 py-4 border-t border-white/10 space-y-2">
        {isAdmin && (
          <NavLink
            to="/settings"
            onClick={() => setSidebarOpen(false)}
            className={({ isActive: a }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${a ? 'bg-white/20 text-white' : 'text-white/60 hover:bg-white/10 hover:text-white'}`
            }
           
            data-testid="nav-settings"
          >
            <Settings size={16} />
            Paramètres
          </NavLink>
        )}

        {/* Bouton Mode Terrain — visible si admin ou tout droit terrain */}
        {(isAdmin || (Array.isArray(menuRights) && menuRights.some(r => r.startsWith('terrain_')))) && (
          <NavLink
            to="/terrain"
            onClick={() => setSidebarOpen(false)}
            data-testid="nav-terrain"
            className={({ isActive: a }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${a ? 'text-white' : 'text-white/70 hover:text-white'}`
            }
            style={{ background: 'rgba(124, 58, 237, 0.25)' }}
          >
            <Tablet size={16} />
            Mode Terrain
          </NavLink>
        )}

        {/* User info + cloche sur la même ligne */}
        <div className="flex items-center gap-1 px-1 py-1">
          <div className="flex-1 min-w-0">
            <UserPopover />
          </div>
          {/* Cloche notifications */}
          <div className="relative flex-shrink-0" ref={notifMenuRef}>
            <style>{`
              @keyframes bellRing {
                0%   { transform: rotate(0deg); }
                10%  { transform: rotate(18deg); }
                20%  { transform: rotate(-16deg); }
                30%  { transform: rotate(14deg); }
                40%  { transform: rotate(-10deg); }
                50%  { transform: rotate(6deg); }
                60%  { transform: rotate(-4deg); }
                70%  { transform: rotate(2deg); }
                80%  { transform: rotate(0deg); }
                100% { transform: rotate(0deg); }
              }
              .bell-ring {
                display: inline-flex;
                transform-origin: top center;
                animation: bellRing 0.8s ease 0s 3 both;
              }
            `}</style>
            <button
              onClick={() => setShowNotifs(v => !v)}
              className="relative text-white/50 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/10"
              title="Notifications"
              data-testid="notif-bell-btn">
              <span className={(nonLues + totalUnreadDiscussions) > 0 ? 'bell-ring' : ''} key={nonLues + totalUnreadDiscussions}>
                <Bell size={16} />
              </span>
              {(nonLues + totalUnreadDiscussions) > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] rounded-full text-white flex items-center justify-center font-bold"
                  style={{ background: '#EF4444', fontSize: 9, padding: '0 3px' }}>
                  {(nonLues + totalUnreadDiscussions) > 9 ? '9+' : (nonLues + totalUnreadDiscussions)}
                </span>
              )}
            </button>
            {showNotifs && (
              <div className="fixed bottom-14 left-3 w-80 bg-white rounded-xl shadow-2xl border overflow-hidden z-50"
                style={{ borderColor: 'var(--border)' }}>
                {/* En-tête */}
                <div className="flex items-center justify-between px-3 py-2.5 border-b" style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Notifications</p>
                  {nonLues > 0 && (
                    <button onClick={handleMarkAll} className="text-xs font-medium" style={{ color: 'var(--primary-solid)' }}>
                      Tout marquer lu
                    </button>
                  )}
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {/* ── Section Discussions non lues ── */}
                  {unreadThreads.length > 0 && (
                    <>
                      <div className="px-3 py-1.5 border-b flex items-center gap-1.5"
                        style={{ borderColor: 'var(--border-light)', background: '#EFF6FF' }}>
                        <MessageCircle size={12} style={{ color: '#1D4ED8' }} />
                        <p className="text-xs font-semibold" style={{ color: '#1D4ED8' }}>
                          Messages non lus ({totalUnreadDiscussions})
                        </p>
                      </div>
                      {unreadThreads.map(t => {
                        const path = t.record_id && DISC_NAV_MAP[t.record_type]
                          ? DISC_NAV_MAP[t.record_type](t.record_id)
                          : '/dashboard';
                        return (
                          <button key={t.thread_id}
                            onClick={() => { navigate(path); setShowNotifs(false); setSidebarOpen(false); }}
                            className="w-full flex items-start gap-2.5 px-3 py-3 text-left hover:bg-blue-50 border-b transition-colors"
                            data-testid={`unread-thread-${t.thread_id}`}
                            style={{ borderColor: 'var(--border-light)', background: '#F0F7FF' }}>
                            <div className="flex-shrink-0 w-2 h-2 rounded-full mt-1.5" style={{ background: '#1D4ED8' }} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1">
                                <p className="text-xs font-semibold truncate" style={{ color: 'var(--text)' }}>
                                  {t.record_label || 'Discussion'}
                                </p>
                                <span className="flex-shrink-0 min-w-[16px] h-[16px] rounded-full text-white flex items-center justify-center font-bold"
                                  style={{ background: '#1D4ED8', fontSize: 9, padding: '0 3px' }}>
                                  {t.unread_count}
                                </span>
                              </div>
                              {t.last_auteur && (
                                <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--text-muted)' }}>
                                  <span className="font-medium">{t.last_auteur} : </span>
                                  {(t.last_corps || '').slice(0, 70)}
                                </p>
                              )}
                            </div>
                            <ExternalLink size={11} className="flex-shrink-0 mt-0.5" style={{ color: '#93C5FD' }} />
                          </button>
                        );
                      })}
                    </>
                  )}

                  {/* ── Section Notifications système ── */}
                  {(() => {
                    const systemNotifs = notifs.filter(n => n.type !== 'discussion_message');
                    return (
                      <>
                        {unreadThreads.length > 0 && systemNotifs.length > 0 && (
                          <div className="px-3 py-1.5 border-b"
                            style={{ borderColor: 'var(--border-light)', background: 'var(--bg-subtle)' }}>
                            <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Alertes système</p>
                          </div>
                        )}
                        {systemNotifs.length === 0 && unreadThreads.length === 0 ? (
                          <p className="text-xs text-center py-6" style={{ color: 'var(--text-muted)' }}>Aucune notification</p>
                        ) : systemNotifs.map(n => (
                          <div key={n.notif_id}
                            className="relative flex items-start gap-2.5 px-3 py-3 border-b hover:bg-gray-50 transition-colors group"
                            style={{ borderColor: 'var(--border-light)', background: n.lu ? 'white' : '#EFF6FF' }}>
                            <button className="flex items-start gap-2.5 flex-1 min-w-0 text-left" onClick={() => handleNotifClick(n)}>
                              <div className="flex-shrink-0 w-2 h-2 rounded-full mt-1.5" style={{ background: n.lu ? 'transparent' : 'var(--primary-solid)' }} />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold" style={{ color: 'var(--text)' }}>{n.titre}</p>
                                <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--text-muted)' }}>{n.message}</p>
                                <p className="text-xs mt-1" style={{ color: 'var(--text-disabled)' }}>
                                  {new Date(n.created_at).toLocaleString('fr-FR', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                                </p>
                              </div>
                            </button>
                            <button
                              onClick={(e) => handleDeleteNotif(e, n.notif_id, n.lu)}
                              data-testid={`delete-notif-${n.notif_id}`}
                              className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-red-100"
                              title="Supprimer"
                              style={{ color: '#EF4444' }}>
                              <X size={13} />
                            </button>
                          </div>
                        ))}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--bg)' }}>
      {/* Modal changement de mot de passe */}
      {showChangePwd && (
        <ChangePasswordModal onClose={() => setShowChangePwd(false)} />
      )}
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 flex-shrink-0 overflow-hidden" style={{ background: 'var(--primary-gradient)' }}>
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="relative flex flex-col w-64 flex-shrink-0 overflow-hidden z-50" style={{ background: 'var(--primary-gradient)' }}>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile topbar */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b bg-white" style={{ borderColor: 'var(--border)' }}>
          <button onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
            <Menu size={22} style={{ color: 'var(--primary-solid)' }} />
          </button>
          <div className="flex items-center gap-2">
            <Wrench size={18} style={{ color: 'var(--primary-solid)' }} />
            <span className="font-bold text-sm" style={{ color: 'var(--primary-solid)' }}>Bedee</span>
          </div>
          <div className="w-6" />
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-3 sm:p-6" data-testid="main-content">
          <AnnouncementBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
